<script>
  import { fly } from "svelte/transition";
  import { tick } from "svelte";
  import { styles } from "./styles";
  import { tourFunctions } from "../tour-functions";
  import { trackCallToActionClick } from "./analytics";
  import Popup from "./Popup.svelte";
  import { watchSlateSubmitInlineStyles } from "./slateFormUtils.js";

  export let callToAction = {
    enabled: false,
    type: "url",
    label: "Request Info",
    url: null,
    embedCode: null,
    bgColor: "#000000",
    textColor: "#ffffff",
    hoverBgColor: "#ffffff",
    hoverTextColor: "#000000",
    fontSize: 12,
    mobileEnabled: true,
    // Popup fields
    popupEnabled: false,
    popupTitle: null,
    popupWidth: 80,
    popupBgColor: null,
    popupTextColor: null,
  };

  // Mobile detection
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";
  const allowMobile = callToAction.mobileEnabled ?? true;
  const enabled = callToAction.enabled && (isMobile ? allowMobile : true);

  let isModalOpen = false;
  let embedContainer;
  let slateStyleCleanup = null;
  
  function handleClick() {
    // Track the CTA click
    const scene_id = tourFunctions.currentScene?.getId();
    const scene_title = tourFunctions.currentScene?.getTitle();
    trackCallToActionClick({
      link_text: callToAction.label,
      link_url: callToAction.url,
      scene_id,
      scene_title,
    });

    if (callToAction.type === "embed") {
      // Open modal - popup will be shown if popupEnabled is true
      openModal();
    } else {
      tourFunctions.focus();
    }
  }

  async function openModal() {
    isModalOpen = true;
    // Wait for DOM to update, then execute scripts
    await tick();
    loadEmbedCode();
  }

  function closeModal() {
    isModalOpen = false;
    if (slateStyleCleanup) {
      slateStyleCleanup();
      slateStyleCleanup = null;
    }
    tourFunctions.focus();
  }

  function getPopupTextColor() {
    // Use popup-specific color if popup is enabled, otherwise fall back to CSS variable
    if (callToAction.popupEnabled && callToAction.popupTextColor) {
      return callToAction.popupTextColor;
    }
    const rootStyles = getComputedStyle(document.documentElement);
    const popupTextColor = rootStyles.getPropertyValue(
      "--form-popup-text-color",
    );
    return popupTextColor || "#000";
  }

  function getPopupBgColor() {
    // Use popup-specific color if popup is enabled, otherwise fall back to CSS variable
    if (callToAction.popupEnabled && callToAction.popupBgColor) {
      return callToAction.popupBgColor;
    }
    const rootStyles = getComputedStyle(document.documentElement);
    const popupBgColor = rootStyles.getPropertyValue(
      "--form-popup-bg-color",
    );
    return popupBgColor || "#fff";
  }

  function getPopupWidth() {
    if (!callToAction.popupEnabled || !callToAction.popupWidth) {
      return "80vw";
    }
    const width = callToAction.popupWidth;
    if (width <= 100) return `${width}vw`;
    return `${width}px`;
  }

  function getPopupTitle() {
    return callToAction.popupEnabled && callToAction.popupTitle
      ? callToAction.popupTitle
      : callToAction.label;
  }

  function loadEmbedCode() {
    if (!embedContainer || !callToAction.embedCode) return;

    if (slateStyleCleanup) {
      slateStyleCleanup();
      slateStyleCleanup = null;
    }
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(callToAction.embedCode, "text/html");
    const formEmbed = document.getElementById("form-embed");
    
    if (!formEmbed) return;
    formEmbed.innerHTML = "";

    doc.body.childNodes.forEach((node) => {
      if (node.nodeName.toLowerCase() === "script") {
        const script = document.createElement("script");

        if (node.src) {
          script.src = node.src;
        } else {
          script.textContent = node.textContent;
        }

        Array.from(node.attributes).forEach((attr) => {
          if (attr.name !== "src") {
            script.setAttribute(attr.name, attr.value);
          }
        });

        formEmbed.appendChild(script);
      } else {
        const newNode = document.importNode(node, true);
        formEmbed.appendChild(newNode);
      }
    });

    slateStyleCleanup = watchSlateSubmitInlineStyles(formEmbed);
  }
</script>

{#if enabled}
  {#if callToAction.type === "url" && callToAction.url}
    <a
      use:styles={callToAction}
      transition:fly={{ y: -300, duration: 500 }}
      href={callToAction.url}
      on:click={handleClick}
      target="_blank"
      class="cta-button">{callToAction.label}</a
    >
  {:else if callToAction.type === "embed" && callToAction.embedCode}
    <button
      use:styles={callToAction}
      transition:fly={{ y: -300, duration: 500 }}
      class="cta-button"
      on:click={handleClick}>{callToAction.label}</button
    >
    {#if isModalOpen && callToAction.popupEnabled}
      <Popup 
        bind:visible={isModalOpen}
        on:close={closeModal}
        ariaLabel={getPopupTitle()}
        width={getPopupWidth()}
        textColor={getPopupTextColor()}
        bgColor={getPopupBgColor()}
      >
        {#if callToAction.popupTitle}
          <h2 class="title">{callToAction.popupTitle}</h2>
        {/if}
        <div class="form-content" bind:this={embedContainer}>
          <div id="form-embed"></div>
        </div>
      </Popup>
    {:else if isModalOpen && !callToAction.popupEnabled}
      <!-- When popup is disabled, execute embed code in a simple div -->
      <div bind:this={embedContainer}>
        <div id="form-embed"></div>
      </div>
    {/if}
  {/if}
{/if}

<style>
  .cta-button {
    position: absolute;
    z-index: 900;
    top: 20px;
    right: 72px;
    display: inline-block;
    cursor: pointer;
    font-family: var(--font-body), sans-serif;
    font-weight: 700;
    line-height: 40px;
    color: var(--textColor);
    background-color: var(--bgColor);
    padding: 3px 16px;
    text-align: center;
    text-transform: uppercase;
    border-radius: 5px;
    border: none;
    text-decoration: none;
    font-size: calc(var(--fontSize) * 1px);
  }

  .cta-button:hover {
    color: var(--hoverTextColor);
    background-color: var(--hoverBgColor);
  }

  .form-content {
    padding-bottom: 100px;
  }

  .title {
    font-family: var(--font-heading), sans-serif;
    font-size: 2.5rem;
    line-height: 1;
    margin-bottom: 1em;
  }

  /* Form Styles - matching FormPopup */
  :global(#form-embed h2) {
    font-size: 2em;
    font-family: var(--font-heading), sans-serif;
  }

  :global(#form-embed .form_question) {
    padding: 0 0.5em;
  }

  :global(#form-embed label) {
    margin-bottom: 0.5em;
    font-weight: 500;
  }

  :global(#form-embed input[type="text"]),
  :global(#form-embed input[type="email"]),
  :global(#form-embed input[type="tel"]),
  :global(#form-embed select),
  :global(#form-embed textarea) {
    width: 100%;
    padding: 12px 20px;
    margin-bottom: 15px;
    box-sizing: border-box;
    border: 2px solid #aaa;
    border-radius: 5px;
    font-size: 16px;
    font-family: var(--font-body), sans-serif;
    outline: none;
  }

  :global(#form-embed button),
  :global(#form-embed input[type="submit"]),
  :global(#form-embed .form_button_submit),
  :global(#form-embed .default.form_button_submit) {
    padding: 10px 20px;
    background-color: var(--form-popup-text-color) !important;
    color: var(--form-popup-bg-color) !important;
    border: none;
    border-radius: 5px;
    font-size: 18px;
    font-family: var(--font-body), sans-serif;
    cursor: pointer;
    transition: background-color 0.3s ease;
    outline: none;
  }

  :global(#form-embed button:hover),
  :global(#form-embed input[type="submit"]:hover),
  :global(#form-embed .form_button_submit:hover),
  :global(#form-embed .default.form_button_submit:hover) {
    background-color: #666 !important;
  }

  :global(#form-embed textarea) {
    min-height: 150px;
  }

  :global(#form-embed input:focus-visible),
  :global(#form-embed textarea:focus-visible),
  :global(#form-embed select:focus-visible),
  :global(#form-embed button:focus-visible) {
    outline: 4px solid var(--focus-color);
    outline-offset: 2px;
    border-radius: 0 !important;  
  }

  /* Fixes for Slate Forms */
  :global(div.suggest) { 
    /* fixes suggestions appearing under the popup */
    z-index: 99999 !important;
  }

  @media (max-width: 768px) {
    .cta-button {
      font-size: 12px;
      padding: 3px 10px;
    }
  }
</style>
